import { useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSessionEngine, type ExerciseKind, type FirstCardNote, type SessionCard, type TrainingResult } from '../model';
import type { BossPoolItem } from '../api/sessionApi';
import { BossIntro } from './BossIntro';
import { Completion } from './Completion';
import { MatchBoss } from './MatchBoss';
import { SessionStage } from './stage/SessionStage';
import { StageAction, StageChip, StageFinish, StageTop } from './stage/StageTop';
import type { SessionVariant } from './variant';

interface SessionScreenProps {
  onExit: () => void;
  variant: SessionVariant;
  /** Совместимость со старыми входами в тренировку. */
  cram?: boolean;
  /** Модуль тренировки (резюм по cram-дорожке per-module). */
  moduleId?: string;
  /** Выход после просмотра результатов. */
  onFinish?: () => void;
  /** Свой экран итогов (знакомство): вместо Completion тренировка один раз отдаёт итог сюда. */
  onComplete?: (result: TrainingResult) => void;
  /** Подсказка до первого ответа (знакомство): «не знаешь слово — …». */
  firstCardNote?: FirstCardNote;
  /** Первая карточка — этим упражнением (знакомство: выбор из вариантов). */
  firstExercise?: ExerciseKind;
}

/** Boss-пул → SessionCard-shape для MatchBoss. id = stateId (НЕ cardId): у
 *  multi-POS слова два state'а на один cardId, и по cardId они бы схлопнулись. */
function bossCards(pool: BossPoolItem[]): SessionCard[] {
  return pool.map((b) => ({
    itemId: b.stateId,
    userCardStateId: b.stateId,
    cardId: b.cardId,
    id: b.stateId,
    en: b.frontName,
    ipa: '',
    ru: b.ru,
    state: 'review',
    cloze: null,
    isLlmGenerated: true,
    pos: b.pos,
    selectedTranslations: [b.ru],
    senseTranslations: [b.ru],
    rareTranslations: [],
    selectionOptions: null,
    hint: null,
    definition: null,
    exampleEn: null,
    simpleExample: b.sentence,
    simpleExampleSpans: b.sentenceSpans,
    simpleExampleRu: null,
    simpleExampleRuSpans: [],
    enSynonyms: [],
    positionInQueue: 0,
    totalPending: 0,
  }));
}

/** Сцена без шапки — для загрузки, пустого состояния и ошибок. */
function PlainStage({ children }: { children: ReactNode }) {
  return (
    <div className="lx-stage">
      <span className="st-wash" aria-hidden />
      <main className="st-body st-body-plain">{children}</main>
    </div>
  );
}

/**
 * Общий рендерер тренировки: один движок (useSessionEngine) и одна сцена на все
 * этапы — упражнения → финальный раунд → итоги. Десктоп и мобила отличаются
 * только раскладкой дока ответа (variant), остальное разводит CSS.
 */
export function SessionScreen({
  onExit, variant, cram, moduleId, onFinish, onComplete, firstCardNote, firstExercise,
}: SessionScreenProps) {
  const { t } = useTranslation();
  const withKeyboard = variant === 'desktop';
  const e = useSessionEngine({ onExit, cram, moduleId, onFinish, autoFocus: withKeyboard, firstExercise });

  // Итог отдаём один раз: ref переживает повтор эффекта в StrictMode.
  const completedRef = useRef(false);
  const isDone = e.mode === 'done';
  const { trainingResult } = e;
  useEffect(() => {
    if (!isDone || !onComplete || completedRef.current) return;
    completedRef.current = true;
    onComplete(trainingResult);
  }, [isDone, onComplete, trainingResult]);

  if (e.loadPhase === 'loading') {
    return <PlainStage>{t('session.loading', 'Загружаем сессию…')}</PlainStage>;
  }

  if (e.loadPhase === 'empty') {
    return (
      <PlainStage>
        <h1 className="st-done-title">{t('session.emptyTitle', 'Тренировка не найдена')}</h1>
        <p className="st-done-sub">
          {t('session.emptySub', 'Выбери модуль и начни новую тренировку.')}
        </p>
        <button type="button" className="st-cta" onClick={e.onExit}>
          {t('session.exit', 'Выход')}
        </button>
      </PlainStage>
    );
  }

  if (e.loadPhase === 'error' || !e.session) {
    return (
      <PlainStage>
        <h1 className="st-done-title">{t('session.errorTitle', 'Не удалось загрузить сессию')}</h1>
        <div className="st-done-actions">
          <button type="button" className="st-cta" onClick={e.onReload}>
            {t('cram.retry', 'Попробовать снова')}
          </button>
          <button type="button" className="st-ghost-cta" onClick={e.onExit}>
            {t('session.exit', 'Выход')}
          </button>
        </div>
      </PlainStage>
    );
  }

  const busy = e.finishing || e.phase === 'submitting';

  if (e.mode === 'bossIntro') {
    return (
      <div className="lx-stage" data-mood="idle">
        <span className="st-wash" aria-hidden />
        <StageTop
          left={<StageChip>{t('session.boss.eyebrow', 'финальный раунд')}</StageChip>}
          right={
            <>
              <StageAction onClick={e.onExit} disabled={busy}>
                {t('session.exitShort', 'выйти')}
              </StageAction>
              <StageFinish onFinish={e.onFinish} disabled={busy} finishing={e.finishing} />
            </>
          }
        />
        <BossIntro
          wordCount={e.bossPool.length}
          onStart={e.startBoss}
          withKeyboard={withKeyboard}
        />
      </div>
    );
  }

  if (e.mode === 'boss') {
    return (
      <div className="lx-stage" data-mood="idle">
        <span className="st-wash" aria-hidden />
        {e.completionError && (
          <div className="st-banner danger" role="alert">
            <span>{e.completionError}</span>{' '}
            <button type="button" className="underline" disabled={e.finishing} onClick={e.onRetryComplete}>
              {t('session.retryComplete', 'Сохранить результаты')}
            </button>
          </div>
        )}
        {e.finishing && (
          <div className="st-banner" role="status">
            {t('session.finishing', 'Завершаем…')}
          </div>
        )}
        <MatchBoss
          sessionCards={bossCards(e.bossPool)}
          onDone={e.onBossDone}
          onMatch={e.onBossMatch}
          onMiss={e.onBossMiss}
          onExit={e.onExit}
          onFinish={e.onFinish}
          finishing={e.finishing}
          busy={busy}
        />
      </div>
    );
  }

  if (e.mode === 'done') {
    // Итоги покажет вызывающий экран (onComplete уже ушёл в эффекте) — без мигания Completion.
    if (onComplete) return <PlainStage>{null}</PlainStage>;
    return (
      <div className="lx-stage" data-mood="done">
        <span className="st-wash" aria-hidden />
        <Completion
          stats={e.stats}
          bossStats={e.bossStats}
          bestCombo={e.bestCombo}
          mistakeWords={e.mistakeWords}
          elapsedMs={e.elapsedMs}
          onExit={e.onResultsExit}
          withKeyboard={withKeyboard}
          streak={e.streak}
          tomorrow={e.dayPlan}
        />
      </div>
    );
  }

  return <SessionStage e={e} variant={variant} firstCardNote={firstCardNote} />;
}
